/**
 * Sidebar — левый навигационный рельс (.rail) дизайн-системы КОММА.
 *
 *   ┌─────────────┐
 *   │ [logo] Кард… │  бренд (.rail__brand)
 *   ├─────────────┤
 *   │ РАЗДЕЛЫ      │  группы навигации (.rail__group)
 *   │  • Сегодня   │
 *   │  • Сегменты  │
 *   │  …           │
 *   │             │
 *   │ [avatar] me │  карточка пользователя (низ)
 *   └─────────────┘
 *
 * Бренд продукта («Кардиограмма клуба» / ClubFirst) сохранён; визуальный
 * язык — КОММА. См. styles/global.css (.rail, .nav-link).
 */

import { useMemo } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Avatar, Icon, type IconName } from '@/shared/ui';
import { useAuth } from '@/auth/useAuth';

interface NavLeaf {
  to: string;
  label: string;
  icon: IconName;
}
interface NavGroup {
  label: string;
  items: NavLeaf[];
}

const ROLE_LABEL: Record<string, string> = {
  admin: 'Администратор',
  curator: 'Куратор',
  seo: 'SEO-специалист',
};

function buildNav(role: string | undefined): NavGroup[] {
  const isAdmin = role === 'admin';
  const isSeo = role === 'seo';
  const groups: NavGroup[] = [
    {
      label: 'Продвижение',
      items: [
        { to: '/overview', label: 'Обзор', icon: 'Dashboard' },
        { to: '/pipe', label: 'Продажи', icon: 'Kanban' },
        { to: '/campaigns', label: 'Кампании', icon: 'Activity' },
        { to: '/anomalies', label: 'Аномалии', icon: 'Alert' },
        { to: '/antifraud', label: 'Антифрод / CRM', icon: 'Shield' },
        { to: '/search-terms', label: 'Поисковые запросы', icon: 'Search' },
        { to: '/semantics', label: 'Семантика', icon: 'Hash' },
        { to: '/competitors', label: 'Конкуренты', icon: 'Eye' },
        { to: '/ai', label: 'AI-помощник', icon: 'Message' },
        { to: '/memory', label: 'Память', icon: 'Archive' },
      ],
    },
  ];
  // Группа SEO — видна seo/admin (curator работает через рекламный контур).
  if (isSeo || isAdmin) {
    groups.push({
      label: 'SEO',
      items: [
        { to: '/seo', label: 'Обзор', icon: 'Dashboard' },
        { to: '/seo/positions', label: 'Позиции', icon: 'ArrowUp' },
        { to: '/seo/keyword-map', label: 'Семантика/карта', icon: 'Globe' },
        { to: '/seo/content', label: 'Контент', icon: 'FileText' },
        { to: '/seo/audits', label: 'Аудиты', icon: 'Shield' },
        { to: '/seo/measure', label: 'Органика → MQL', icon: 'Activity' },
        { to: '/seo/competitors', label: 'Конкуренты (орг)', icon: 'Eye' },
        { to: '/seo/tasks', label: 'Задачи', icon: 'Inbox' },
      ],
    });
  }
  if (isAdmin) {
    groups.push({
      label: 'Администрирование',
      items: [{ to: '/users', label: 'Пользователи', icon: 'Users' }],
    });
  }
  return groups;
}

function isItemActive(pathname: string, to: string): boolean {
  // /overview и /seo — «индексные» пункты: активны только при точном совпадении
  // (иначе /seo светился бы на всех /seo/*).
  const exactOnly = to === '/overview' || to === '/seo';
  return pathname === to || (!exactOnly && pathname.startsWith(to + '/'));
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth();
  const { location } = useRouterState();
  const pathname = location.pathname;
  const groups = useMemo(() => buildNav(user?.role), [user?.role]);

  return (
    <aside className="rail">
      <div className="rail__brand">
        <div
          aria-hidden
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 800,
            fontSize: 15,
            color: 'var(--on-primary, #fff)',
            background: 'var(--primary)',
            boxShadow: 'var(--e-primary-sm)',
          }}
        >
          K
        </div>
        <span className="rail__name">
          KOMMA Ads<small>Продвижение</small>
        </span>
      </div>

      <div className="rail__group">
        {groups.map((group) => (
          <div key={group.label}>
            <div className="rail__group-label">{group.label}</div>
            {group.items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={onNavigate}
                aria-current={isItemActive(pathname, it.to) ? 'page' : undefined}
                className={'nav-link' + (isItemActive(pathname, it.to) ? ' active' : '')}
              >
                <Icon name={it.icon} />
                <span>{it.label}</span>
              </Link>
            ))}
          </div>
        ))}
      </div>

      <div className="grow" />

      {user && (
        <div style={{ padding: 14 }}>
          <div
            className="row center gap10"
            style={{
              padding: '11px 12px',
              borderRadius: 'var(--r-md)',
              background: 'var(--surface-alt)',
              border: '1px solid var(--border)',
            }}
          >
            <Avatar name={user.display_name} size={36} />
            <div className="col grow" style={{ gap: 0, minWidth: 0 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user.display_name}
              </span>
              <span
                className="muted mono"
                style={{
                  fontSize: 10.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {ROLE_LABEL[user.role] ?? user.role} · @{user.username}
              </span>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
